import type { PublicRuntimeConfig } from "@growthwise/core";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { getDeploymentRegion } from "../adapters/registry";
import { api } from "../services/api-client";

/**
 * Limits used while the server's own values are still loading.
 *
 * Intentionally conservative: if the real limits never arrive, the UI
 * offers less than the server allows rather than more, so it can never
 * invite an upload the server will reject.
 */
const FALLBACK: PublicRuntimeConfig = {
  deploymentRegion: "international",
  authProvider: "unknown",
  storageProvider: "unknown",
  antiAbuseProvider: "unknown",
  antiAbuseEnabled: false,
  video: {
    maxBytes: 50 * 1024 * 1024,
    maxDurationMs: 60_000,
    maxActiveVideosPerUser: 3,
    allowedMimeTypes: ["video/webm", "video/mp4"],
  },
};

interface ConfigContextValue {
  config: PublicRuntimeConfig;
  /** False until the server's values have been fetched. */
  loaded: boolean;
  /** True when the API could not be reached at all. */
  unavailable: boolean;
}

const ConfigContext = createContext<ConfigContextValue>({
  config: FALLBACK,
  loaded: false,
  unavailable: false,
});

/**
 * Loads the public runtime configuration.
 *
 * Recording limits are read from the server rather than duplicated in the
 * frontend, so the maximum length and size the recorder enforces always
 * match what the API and the storage rules will actually accept.
 */
export const ConfigProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<ConfigContextValue>({
    config: { ...FALLBACK, deploymentRegion: getDeploymentRegion() },
    loaded: false,
    unavailable: false,
  });

  useEffect(() => {
    let cancelled = false;

    api
      .getPublicConfig()
      .then((config) => {
        if (!cancelled) setState({ config, loaded: true, unavailable: false });
      })
      .catch(() => {
        if (!cancelled) {
          setState((current) => ({
            ...current,
            loaded: false,
            unavailable: true,
          }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <ConfigContext.Provider value={state}>{children}</ConfigContext.Provider>
  );
};

export const useRuntimeConfig = (): ConfigContextValue =>
  useContext(ConfigContext);
